import { Briefcase, Building2, SquareUser } from "lucide-react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@workos-inc/authkit-react";

import { hasAnyAdminAccess } from "@/shared/config/adminPermissions";
import { isHourglassInternal } from "@/shared/config/hourglassPermissions";
import { Tabs, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";
import { cn } from "@/shared/lib/utils";

export type Workspace = "timekeeper" | "admin" | "internal";

interface WorkspaceSwitcherProps {
	current: Workspace;
}

const ROUTES: Record<Workspace, string> = {
	timekeeper: "/entries",
	admin: "/admin",
	internal: "/internal",
};

const LABELS: Record<Workspace, string> = {
	timekeeper: "Timekeeper",
	admin: "Admin",
	internal: "Internal",
};

const ICONS: Record<Workspace, React.ElementType> = {
	timekeeper: Briefcase,
	admin: Building2,
	internal: SquareUser,
};

/**
 * Tab strip that lets users switch between the workspaces their email
 * grants them access to. Renders nothing if the user only has access to
 * one workspace (no point in a switcher with a single tab).
 *
 * Always shows icon + text. The sidebar is wide enough for all available
 * workspaces, including Hourglass internal staff with three tabs.
 *
 * Expanded-mode only — each sidebar keeps its own collapsed-mode icon
 * button for the previous workspace, matching the pre-extraction pattern.
 */
export function WorkspaceSwitcher({ current }: WorkspaceSwitcherProps) {
	const navigate = useNavigate();
	const { user } = useAuth();

	const workspaces = useMemo<Workspace[]>(() => {
		const result: Workspace[] = ["timekeeper"];
		if (hasAnyAdminAccess(user?.email)) result.push("admin");
		if (isHourglassInternal(user?.email)) result.push("internal");
		return result;
	}, [user?.email]);

	if (workspaces.length < 2) return null;

	return (
		<div className="mt-2 -mx-4 px-2 group-data-[collapsible=icon]:hidden">
			<Tabs
				value={current}
				onValueChange={(value) => {
					const next = value as Workspace;
					if (next !== current) navigate(ROUTES[next]);
				}}
			>
				<TabsList className="flex bg-transparent rounded-none border-b border-sidebar-border w-full gap-0 px-2 py-0 h-auto">
					{workspaces.map((ws) => {
						const Icon = ICONS[ws];
						return (
							<TabsTrigger
								key={ws}
								value={ws}
								aria-label={LABELS[ws]}
								className={cn(
									"flex-1 rounded-none border-b-2 border-transparent bg-transparent px-2 py-1.5 text-xs text-sidebar-foreground/60 shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:bg-transparent data-[state=active]:text-sidebar-foreground data-[state=active]:shadow-none gap-1.5",
								)}
							>
								<Icon className="size-3.5" />
								{LABELS[ws]}
							</TabsTrigger>
						);
					})}
				</TabsList>
			</Tabs>
		</div>
	);
}
